import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import MatrixBackground from "../components/MatrixBackground";
import SocialIcon from "../components/SocialIcon";
import AsciiIntro from "../components/AsciiIntro";

const roles = [
  "Frontend Engineer ⚛️",
  "JavaScript Junkie 💻",
  "Open-Source Operative 🛠️ ",
  "AI isn’t the future. It’s now 🚀",
  "Always Red Pill 🔴",
];

const Home = () => {
  const [roleIndex, setRoleIndex] = useState(0);
  const [typedRole, setTypedRole] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showContent, setShowContent] = useState(false);

  // Intro logic
  useEffect(() => {
    const hasVisited = localStorage.getItem("hasVisited");
    const skipIntroParam =
      new URLSearchParams(window.location.search).get("skipIntro") === "true";
    if (hasVisited || skipIntroParam) {
      setShowIntro(false);
      setShowContent(true);
    } else {
      const t = setTimeout(() => {
        setShowIntro(false);
        setShowContent(true);
        localStorage.setItem("hasVisited", "true");
      }, 4000);
      return () => clearTimeout(t);
    }
  }, []);

  // Role typewriter
  useEffect(() => {
    const current = roles[roleIndex];
    let timeout;
    if (!isDeleting) {
      if (typedRole.length < current.length) {
        timeout = setTimeout(
          () => setTypedRole(current.slice(0, typedRole.length + 1)),
          90,
        );
      } else {
        timeout = setTimeout(() => setIsDeleting(true), 2000);
      }
    } else {
      if (typedRole.length > 0) {
        timeout = setTimeout(
          () => setTypedRole(current.slice(0, typedRole.length - 1)),
          55,
        );
      } else {
        setIsDeleting(false);
        setRoleIndex((prev) => (prev + 1) % roles.length);
      }
    }
    return () => clearTimeout(timeout);
  }, [typedRole, isDeleting, roleIndex]);

  const stats = [
    { label: "XP", value: "8+ YEARS", icon: "⚡" },
    { label: "QUESTS", value: "20+ COMPLETED", icon: "🎯" },
    { label: "SKILLS", value: "15+ UNLOCKED", icon: "🛠️" },
  ];

  return (
    <div className="min-h-screen font-mono relative overflow-hidden bg-[#050a0e]">
      <MatrixBackground />

      {/* Ambient orb accents */}
      <div
        className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full opacity-5 pointer-events-none z-[1]"
        style={{
          background: "radial-gradient(circle, #00ff99, transparent 70%)",
          filter: "blur(60px)",
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-5 pointer-events-none z-[1]"
        style={{
          background: "radial-gradient(circle, #06b6d4, transparent 70%)",
          filter: "blur(80px)",
        }}
      />

      {showIntro ? (
        <AsciiIntro />
      ) : (
        showContent && (
          <div className="relative z-10 flex flex-col items-center justify-center min-h-screen text-center px-6">
            {/* Name */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4"
            >
              <span className="text-white">Hi, I'm </span>
              <span className="gradient-text">Pankaj Singh</span>
            </motion.h1>

            {/* Typewriter role */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-lg md:text-2xl mb-8 h-9"
            >
              <span className="text-cyan-400">&gt;&nbsp;</span>
              <span className="text-slate-100 font-mono">{typedRole}</span>
              <span className="cursor-blink text-emerald-400 ml-0.5">▊</span>
            </motion.div>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="text-sm md:text-base text-slate-300/85 max-w-md mb-10 leading-relaxed tracking-wide"
            >
              Crafting performant, pixel-perfect interfaces for mobile & web.
              Specialised in React, React Native &amp; modern JS ecosystems.
            </motion.p>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="flex gap-6 md:gap-8 mb-10"
            >
              {stats.map((s, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="text-xl mb-1">{s.icon}</div>
                  <div className="text-base md:text-lg font-bold gradient-text mb-0.5">
                    {s.value}
                  </div>
                  <div className="text-[10px] text-slate-400/70 tracking-[0.15em] uppercase font-mono">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>

            {/* CTA buttons */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.7 }}
              className="flex gap-4 mb-10"
            >
              <Link
                to="/portfolio-blog"
                className="btn-primary font-mono text-sm"
              >
                &gt; View Work
              </Link>
              <Link
                to="/contact"
                className="relative px-6 py-2.5 rounded text-sm font-semibold tracking-wider transition-all duration-300 font-mono border border-cyan-500/30 text-slate-100 hover:border-cyan-400/60 hover:text-cyan-200 hover:shadow-[0_0_12px_rgba(6,182,212,0.2)]"
              >
                &gt; Contact Me
              </Link>
            </motion.div>

            {/* Social icons */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="flex gap-4"
            >
              {/* GitHub — outline octocat */}
              <SocialIcon
                href="https://github.com/ps0305"
                label="GitHub"
                accent="green"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22"
                  />
                </svg>
              </SocialIcon>

              {/* LinkedIn — outline badge */}
              <SocialIcon
                href="https://www.linkedin.com/in/pankaj0305/"
                label="LinkedIn"
                accent="cyan"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"
                  />
                  <rect
                    x="2"
                    y="9"
                    width="4"
                    height="12"
                    rx="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="4"
                    cy="4"
                    r="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </SocialIcon>
            </motion.div>
          </div>
        )
      )}
    </div>
  );
};

export default Home;
